import { useCallback, useMemo, useRef } from "react";
import { proxy, useSnapshot } from "valtio";
import { useCommandManager } from "../../../hooks/useCommandManager";
import { useFeatureFlagToggle } from "../../../hooks/useFeatureFlags";

/** Shared state */
const photoPinchingState = proxy({
	isPhotoPinching: false,
});

/**
 * Logic to pinch a photo in a zone
 * This intentionally doesn't include the logic to trigger it
 * Could be based on gestures, keyboard, ...
 */
export function usePhotoPinching({
	maxReplaceDelay = 1000,
}: {
	/** When using in a transaction, we can pass Infinity to keep the command replaceable in the undo stack until transaction is closed */
	maxReplaceDelay?: number;
}) {
	const commandManager = useCommandManager();
	const isTransformWithRotateEnabled = useFeatureFlagToggle("transformWithRotate");

	/** An object holding information information for Zooming a photo in a zone */
	const photoPinchingRef = useRef<
		| {
				selectedZone: eXpress.core.PhotoLookZone;
				startNormalizedCrop: eXpress.math.Rect | undefined;
				startPinchDistance: number;
				startPinchOrigin: [x: number, y: number];
				lastCommandId: string;
		  }
		| undefined
	>(undefined);

	const { isPhotoPinching } = useSnapshot(photoPinchingState);

	const start = useCallback(function startPhotoPinching(
		selectedZone: eXpress.core.PhotoLookZone,
		pinchDistance: number,
		pinchOrigin: [x: number, y: number],
	) {
		photoPinchingState.isPhotoPinching = true;

		// Start with new pinching information
		photoPinchingRef.current = {
			selectedZone,
			startNormalizedCrop: selectedZone.getPhotoLook()?.normalizedCrop,
			startPinchDistance: pinchDistance,
			startPinchOrigin: pinchOrigin,
			lastCommandId: "",
		};
	}, []);

	const fixCropSize = useCallback(function zoomFitPhoto(
		minCrop: eXpress.math.Rect,
		maxCrop: eXpress.math.Rect,
	): eXpress.math.Rect[] {
		const photoPinching = photoPinchingRef.current;
		if (!photoPinching) return [minCrop, maxCrop];

		const { startNormalizedCrop, selectedZone } = photoPinching;
		if (!startNormalizedCrop || !selectedZone) return [minCrop, maxCrop];

		const photoLook = selectedZone.getPhotoLook();
		const currentCrop = photoLook?.getCrop();
		if (!photoLook || !currentCrop) return [minCrop, maxCrop];
		const maxCropSize = currentCrop.imageSize.fit(eXpress.math.Vector.ones);
		const minCropSize = maxCropSize.scale(1 / 10);

		const isLeftOrRightRotated =
			photoLook.angle === eXpress.core.Rotate90.Left ||
			photoLook.angle === eXpress.core.Rotate90.Right;

		const imageAspectRatio = currentCrop.imageSize.width / currentCrop.imageSize.height;

		if (imageAspectRatio < 1) {
			if (!isLeftOrRightRotated) {
				minCropSize.width = 1;
				minCropSize.height =
					currentCrop.imageSize.width /
					currentCrop.imageSize.height /
					(selectedZone.width / selectedZone.height);
			} else {
				const temp = {
					width: maxCropSize.width,
					height: maxCropSize.height,
				};
				maxCropSize.width = temp.height;
				maxCropSize.height = temp.width;
				minCropSize.height = 1;
				minCropSize.width =
					(currentCrop.imageSize.height / currentCrop.imageSize.width) *
					(selectedZone.height / selectedZone.width);
			}
		} else if (!isLeftOrRightRotated) {
			const temp = {
				width: maxCropSize.width,
				height: maxCropSize.height,
			};
			maxCropSize.width = temp.height;
			maxCropSize.height = temp.width;
			minCropSize.width = 1;
			minCropSize.height =
				currentCrop.imageSize.width /
				currentCrop.imageSize.height /
				(selectedZone.width / selectedZone.height);
		} else {
			const temp = {
				width: maxCropSize.width,
				height: maxCropSize.height,
			};
			maxCropSize.width = temp.height;
			maxCropSize.height = temp.width;
			minCropSize.height = 1;
			minCropSize.width =
				(currentCrop.imageSize.height / currentCrop.imageSize.width) *
				(selectedZone.height / selectedZone.width);
		}

		return [minCropSize, maxCropSize];
	}, []);

	const update = useCallback(
		function updatePhotoPinching(
			pinchDistance: number,

			_pinchOrigin: [x: number, y: number],
		) {
			const photoPinching = photoPinchingRef.current;
			if (!photoPinching) return;

			const { startNormalizedCrop, startPinchDistance, selectedZone } = photoPinching;
			if (!startNormalizedCrop || !selectedZone) return;

			const photoLook = selectedZone.getPhotoLook();
			const currentCrop = photoLook?.getCrop();
			if (!photoLook || !currentCrop) return;

			const center = startNormalizedCrop.center();
			let maxCropSize = currentCrop.imageSize.fill(eXpress.math.Vector.ones);
			let minCropSize = maxCropSize.scale(1 / 10); // use something based on image size and dpi?

			if (selectedZone.stretchMode === eXpress.core.StretchMode.Fit) {
				const newCrops = fixCropSize(minCropSize, maxCropSize);
				[minCropSize, maxCropSize] = newCrops;
			}

			// TODO: take pinch origin into account to allow moving during pinch
			const newNormalizedCropRect = startNormalizedCrop
				.mul(eXpress.math.Frame.scale(startPinchDistance / pinchDistance, center)) // Used frame to scale at center
				.advancedClamp(minCropSize, maxCropSize); // Advanced clamp will try to keep the center

			const command = new eXpress.core.commands.PanCropCommand(
				photoLook,
				newNormalizedCropRect,
				undefined,
				true,
				maxReplaceDelay,
				isTransformWithRotateEnabled,
			);
			commandManager.exec(command);

			// Remember this command id so we can 'end' it
			photoPinching.lastCommandId = commandManager.getRowId();
		},
		[maxReplaceDelay, isTransformWithRotateEnabled, commandManager, fixCropSize],
	);

	const end = useCallback(
		function endPhotoPinching() {
			if (!photoPinchingRef.current) return;
			if (photoPinchingRef.current.lastCommandId === commandManager.getRowId()) {
				commandManager.makeLastNonInteractive();
			}

			photoPinchingRef.current = undefined;
			photoPinchingState.isPhotoPinching = false;
		},
		[commandManager],
	);

	const cancel = useCallback(
		function cancelPhotoPinching() {
			if (!photoPinchingRef.current) return;
			if (photoPinchingRef.current.lastCommandId === commandManager.getRowId()) {
				commandManager.undo(true);
			}
			photoPinchingRef.current = undefined;
			photoPinchingState.isPhotoPinching = false;
		},
		[commandManager],
	);

	return [
		isPhotoPinching,
		useMemo(
			// Try to return a stable object
			() => ({
				start,
				update,
				end,
				cancel,
			}),
			[start, update, end, cancel],
		),
	] as const;
}
